import { WidgetApi } from './api.js';
import type { ChatOptionDto, PublicConfig } from './types.js';
import { BookingForm } from './booking-form.js';
import { el } from './dom-utils.js';

/**
 * PractiGo embeddable chat widget.
 *
 * Install with:
 *   <script src="https://cdn.practigo.com/widget.js" data-widget-key="PUBLIC_WIDGET_KEY"></script>
 *
 * Optional attributes:
 *   data-api-base-url  — override the API origin (defaults to http://localhost:4000 for local dev)
 *   data-position       — "bottom-right" | "bottom-left" (overrides the clinic's configured default)
 *
 * Framework-free by design so it works unmodified on plain HTML, WordPress,
 * Webflow, React, or Next.js sites — anywhere a <script> tag can be dropped in.
 */

const STYLE_ID = 'practigo-widget-style';
const STORAGE_PREFIX = 'practigo_widget_conversation_';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .pg-widget-launcher {
      position: fixed; z-index: 2147483000; bottom: 20px;
      width: 56px; height: 56px; border-radius: 9999px; border: none;
      box-shadow: 0 8px 24px rgba(0,0,0,0.18); cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      color: #fff; transition: transform 0.15s ease;
    }
    .pg-widget-launcher:hover { transform: scale(1.06); }
    .pg-widget-launcher.right { right: 20px; }
    .pg-widget-launcher.left { left: 20px; }
    .pg-widget-launcher svg { width: 26px; height: 26px; }

    .pg-widget-panel {
      position: fixed; z-index: 2147483000; bottom: 88px;
      width: 392px; max-width: calc(100vw - 32px);
      height: 520px; max-height: calc(100vh - 120px);
      background: #fff; border-radius: 16px; overflow: hidden;
      box-shadow: 0 16px 48px rgba(0,0,0,0.24);
      display: flex; flex-direction: column;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      opacity: 0; transform: translateY(12px); pointer-events: none;
      transition: opacity 0.15s ease, transform 0.15s ease;
    }
    .pg-widget-panel.right { right: 20px; }
    .pg-widget-panel.left { left: 20px; }
    .pg-widget-panel.open { opacity: 1; transform: translateY(0); pointer-events: auto; }

    .pg-widget-header {
      padding: 14px 16px; color: #fff; display: flex; align-items: center; justify-content: space-between;
    }
    .pg-widget-header-title { font-weight: 600; font-size: 15px; }
    .pg-widget-header-sub { font-size: 12px; opacity: 0.85; }
    .pg-widget-close {
      background: transparent; border: none; color: #fff; font-size: 20px; cursor: pointer; line-height: 1;
      padding: 4px;
    }

    .pg-widget-messages {
      flex: 1; overflow-y: auto; padding: 12px 14px; background: #f8fafc;
      display: flex; flex-direction: column; gap: 8px;
    }
    .pg-msg { max-width: 82%; padding: 9px 12px; border-radius: 14px; font-size: 13.5px; line-height: 1.4; white-space: pre-wrap; }
    .pg-msg.assistant { align-self: flex-start; background: #fff; border: 1px solid #e2e8f0; color: #1e293b; border-bottom-left-radius: 4px; }
    .pg-msg.user { align-self: flex-end; color: #fff; border-bottom-right-radius: 4px; }
    .pg-msg.error { align-self: center; background: #fee2e2; color: #991b1b; font-size: 12px; }
    .pg-msg.emergency { align-self: stretch; background: #fef3c7; color: #92400e; font-weight: 600; border-radius: 10px; }

    .pg-widget-typing { align-self: flex-start; font-size: 12px; color: #94a3b8; padding-left: 4px; }

    .pg-quick-replies { display: flex; flex-wrap: wrap; gap: 6px; align-self: stretch; margin-top: 2px; }
    .pg-quick-reply-btn {
      border: 1px solid #cbd5e1; background: #fff; border-radius: 9999px; padding: 7px 13px;
      font-size: 12.5px; font-weight: 500; cursor: pointer; font-family: inherit; color: #1e293b;
      transition: background 0.12s ease;
    }
    .pg-quick-reply-btn:hover { background: #f1f5f9; }
    .pg-quick-reply-btn:disabled { opacity: 0.5; cursor: not-allowed; }

    .pg-widget-inputbar { display: flex; gap: 8px; padding: 10px; border-top: 1px solid #e2e8f0; background: #fff; }
    .pg-widget-input {
      flex: 1; border: 1px solid #cbd5e1; border-radius: 10px; padding: 9px 11px; font-size: 13.5px; outline: none; resize: none;
    }
    .pg-widget-input:focus { border-color: #94a3b8; }
    .pg-widget-send {
      border: none; border-radius: 10px; padding: 0 16px; color: #fff; font-weight: 600; font-size: 13px; cursor: pointer;
    }
    .pg-widget-send:disabled { opacity: 0.5; cursor: not-allowed; }

    .pg-modal-backdrop {
      position: fixed; inset: 0; z-index: 2147483001; background: rgba(15, 23, 42, 0.45);
      display: flex; align-items: center; justify-content: center; padding: 16px;
      opacity: 0; pointer-events: none; transition: opacity 0.15s ease;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }
    .pg-modal-backdrop.open { opacity: 1; pointer-events: auto; }
    .pg-modal {
      background: #fff; border-radius: 16px; width: 440px; max-width: 100%;
      max-height: calc(100vh - 48px); overflow-y: auto;
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
    }
    .pg-modal-header {
      padding: 16px 20px; color: #fff; display: flex; align-items: center; justify-content: space-between;
      position: sticky; top: 0;
    }
    .pg-modal-header-title { font-weight: 600; font-size: 16px; }
    .pg-modal-close { background: transparent; border: none; color: #fff; font-size: 22px; cursor: pointer; line-height: 1; padding: 4px; }
    .pg-modal-body { padding: 16px 18px; display: flex; flex-direction: column; gap: 10px; }
    .pg-field label { font-size: 12.5px; font-weight: 600; color: #334155; display: block; margin-bottom: 4px; }
    .pg-field select, .pg-field input {
      width: 100%; border: 1px solid #cbd5e1; border-radius: 8px; padding: 7px 10px; font-size: 13.5px;
      font-family: inherit; box-sizing: border-box;
    }
    .pg-field select:focus, .pg-field input:focus { outline: none; border-color: #94a3b8; }
    .pg-slots-list { display: flex; flex-direction: column; gap: 5px; max-height: 220px; overflow-y: auto; }
    .pg-slot-group-header {
      font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.02em;
      padding: 4px 2px 0; margin-top: 2px;
    }
    .pg-slot-group-header:first-child { margin-top: 0; }
    .pg-slot-group { display: flex; flex-wrap: wrap; gap: 5px; }
    .pg-slot-btn {
      border: 1px solid #cbd5e1; background: #fff; border-radius: 8px; padding: 6px 10px; font-size: 12.5px;
      cursor: pointer; text-align: center; font-family: inherit; color: #1e293b;
    }
    .pg-slot-group .pg-slot-btn { flex: 1 1 calc(33.333% - 5px); text-align: center; }
    .pg-slot-btn.selected { color: #fff; border-color: transparent; }
    .pg-slot-empty { font-size: 13px; color: #64748b; padding: 6px 2px; }
    .pg-slot-hint { font-size: 11.5px; color: #64748b; padding: 0 2px 4px; }
    .pg-submit-btn {
      border: none; border-radius: 10px; padding: 10px; color: #fff; font-weight: 600; font-size: 14px;
      cursor: pointer; font-family: inherit;
    }
    .pg-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .pg-form-error { background: #fee2e2; color: #991b1b; font-size: 12.5px; padding: 9px 11px; border-radius: 8px; }
    .pg-form-success { text-align: center; padding: 8px 4px 4px; display: flex; flex-direction: column; align-items: center; gap: 8px; }
    .pg-form-success-icon {
      width: 52px; height: 52px; border-radius: 9999px; background: #dcfce7; color: #16a34a;
      display: flex; align-items: center; justify-content: center; margin-bottom: 4px;
    }
    .pg-form-success-icon svg { width: 26px; height: 26px; }
    .pg-form-success-title { font-weight: 600; font-size: 15px; }
    .pg-form-success-detail { font-size: 13px; color: #475569; }
    .pg-form-success-token { font-size: 13px; font-weight: 600; }

    /* Segmented OTP input — 6 individually-boxed digits with auto-advance,
       instead of one generic 6-char text field. Higher specificity than the
       .pg-field input rule above (which would otherwise force width:100% /
       its own padding onto each box) via the .pg-otp-boxes ancestor class. */
    .pg-otp-boxes { display: flex; gap: 8px; justify-content: center; }
    .pg-otp-boxes input.pg-otp-box {
      width: 42px; height: 48px; padding: 0; text-align: center; font-size: 20px; font-weight: 600;
      border: 1px solid #cbd5e1; border-radius: 8px; font-family: inherit;
    }
    .pg-otp-boxes input.pg-otp-box:focus { outline: none; border-color: #94a3b8; box-shadow: 0 0 0 3px rgba(37,99,235,0.12); }
    .pg-otp-actions { display: flex; justify-content: space-between; gap: 12px; padding: 2px 1px; }
    .pg-otp-link {
      background: none; border: none; padding: 0; font-size: 12.5px; font-weight: 600; color: #2563eb;
      cursor: pointer; font-family: inherit; text-decoration: underline; text-underline-offset: 2px;
    }

    @media (max-width: 420px) {
      .pg-widget-panel { width: calc(100vw - 24px); right: 12px !important; left: 12px !important; bottom: 84px; }
    }
  `;
  document.head.appendChild(style);
}

class PractiGoWidget {
  private api: WidgetApi;
  private position: 'left' | 'right';
  private conversationId: string | null = null;
  private storageKey: string;
  private launcher!: HTMLButtonElement;
  private panel!: HTMLDivElement;
  private messagesEl!: HTMLDivElement;
  private input!: HTMLTextAreaElement;
  private sendBtn!: HTMLButtonElement;
  private isOpen = false;
  private branding: PublicConfig | null = null;

  // Direct booking form (non-chat) state — the modal chrome only; the actual
  // form (doctor/date/slot-or-window/contact/OTP/submit) lives in BookingForm
  // (booking-form.ts), shared with the inline data-practigo-book embeds below.
  private bookingBackdrop!: HTMLDivElement;
  private bookingBody!: HTMLDivElement;
  private isBookingOpen = false;

  constructor(
    private widgetKey: string,
    apiBaseUrl: string,
    positionOverride?: 'left' | 'right',
  ) {
    this.api = new WidgetApi({ widgetKey, apiBaseUrl });
    this.position = positionOverride ?? 'right';
    this.storageKey = `${STORAGE_PREFIX}${widgetKey}`;
  }

  async init() {
    injectStyles();
    this.renderLauncherSkeleton();

    try {
      this.branding = await this.api.getPublicConfig();
      this.applyBranding(this.branding);
    } catch (err) {
      console.error('[PractiGo widget] failed to load clinic configuration', err);
      this.launcher.title = 'Chat is temporarily unavailable';
    }

    await this.mountInlineBookingForms();

    this.launcher.addEventListener('click', () => this.toggle());
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (this.isBookingOpen) this.closeBookingForm();
      else if (this.isOpen) this.toggle();
    });
  }

  /** Public: open the chat panel programmatically, e.g. from a host page's own "Book an appointment" button. */
  async open() {
    if (!this.isOpen) await this.toggle();
  }

  /**
   * Public: open the direct booking form — a structured doctor/date/time +
   * contact-details flow that books straight through the REST API (no AI
   * conversation involved). For a host page that wants its own "Book an
   * appointment" button to go straight to a form instead of the chat bubble.
   */
  async openBookingForm() {
    if (this.isBookingOpen) return;
    this.isBookingOpen = true;
    if (this.bookingBackdrop) this.bookingBackdrop.remove();
    this.buildBookingModal();
    document.body.appendChild(this.bookingBackdrop);
    requestAnimationFrame(() => this.bookingBackdrop.classList.add('open'));
    const form = new BookingForm({
      api: this.api,
      branding: this.branding,
      mount: this.bookingBody,
      onDone: () => this.closeBookingForm(),
    });
    await form.render();
  }

  closeBookingForm() {
    this.isBookingOpen = false;
    if (this.bookingBackdrop) this.bookingBackdrop.classList.remove('open');
  }

  private buildBookingModal() {
    this.bookingBackdrop = el('div', 'pg-modal-backdrop');
    const modal = el('div', 'pg-modal');

    const header = el('div', 'pg-modal-header');
    header.style.background = this.branding?.primaryColor ?? '#2563eb';
    const title = el('div', 'pg-modal-header-title');
    title.textContent = `Book with ${this.branding?.clinicDisplayName ?? 'us'}`;
    const closeBtn = el('button', 'pg-modal-close');
    closeBtn.innerHTML = '&times;';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', () => this.closeBookingForm());
    header.appendChild(title);
    header.appendChild(closeBtn);

    this.bookingBody = el('div', 'pg-modal-body');

    modal.appendChild(header);
    modal.appendChild(this.bookingBody);
    this.bookingBackdrop.appendChild(modal);
    this.bookingBackdrop.addEventListener('click', (e) => {
      if (e.target === this.bookingBackdrop) this.closeBookingForm();
    });
  }

  /**
   * Scans the host page for `<div data-practigo-book>` containers (e.g. a
   * clinic-site page's "Book Now" section) and mounts an independent
   * BookingForm into each — the same real doctor/date/slot-or-window/OTP
   * flow as the modal, just embedded directly in the page instead of behind
   * a floating launcher. Safe to call even if no such container exists.
   */
  private async mountInlineBookingForms() {
    const containers = document.querySelectorAll<HTMLElement>('[data-practigo-book]');
    for (const container of Array.from(containers)) {
      const form = new BookingForm({ api: this.api, branding: this.branding, mount: container });
      await form.render();
    }
  }

  // Crisp, identical-everywhere SVG icons instead of the 💬 emoji glyph
  // (renders as a different image per OS/browser/font, and doesn't visually
  // match a "professional" clinic-branded widget). Chat bubble by default,
  // swapped for an X once the panel is open — see setLauncherIcon below.
  private static readonly CHAT_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>';
  private static readonly CLOSE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';

  private setLauncherIcon(open: boolean) {
    this.launcher.innerHTML = open ? PractiGoWidget.CLOSE_ICON : PractiGoWidget.CHAT_ICON;
  }

  private renderLauncherSkeleton() {
    this.launcher = el('button', `pg-widget-launcher ${this.position}`);
    this.launcher.setAttribute('aria-label', 'Open chat');
    this.setLauncherIcon(false);
    this.launcher.style.background = '#2563eb';
    document.body.appendChild(this.launcher);
    this.syncWaFloatClearance();
  }

  /**
   * A clinic's own WhatsApp deep-link button (`.wa-float`, rendered
   * server-side by the clinic-site page template) sits fixed at the exact
   * same bottom-right corner (bottom:20px; right:20px) this launcher
   * defaults to. Left unchecked, the two stack exactly on top of each
   * other and the WhatsApp button ends up permanently hidden and
   * unclickable underneath this one. When both exist and the launcher is
   * on the right, lift the launcher (and its panel, once built) clear of
   * it instead of covering it.
   */
  private syncWaFloatClearance() {
    const clear = this.position === 'right' && !!document.querySelector('.wa-float');
    this.launcher.style.bottom = clear ? '84px' : '';
    if (this.panel) this.panel.style.bottom = clear ? '152px' : '';
  }

  private applyBranding(branding: PublicConfig) {
    if (branding.widgetPosition) {
      this.position = branding.widgetPosition === 'BOTTOM_LEFT' ? 'left' : this.position;
    }
    this.launcher.className = `pg-widget-launcher ${this.position}`;
    this.launcher.style.background = branding.primaryColor;
    this.launcher.setAttribute('aria-label', `Chat with ${branding.clinicDisplayName}`);
    this.syncWaFloatClearance();
  }

  private buildPanel() {
    this.panel = el('div', `pg-widget-panel ${this.position}`);
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-label', `${this.branding?.clinicDisplayName ?? 'PractiGo'} chat`);

    const header = el('div', 'pg-widget-header');
    header.style.background = this.branding?.primaryColor ?? '#2563eb';
    const titleWrap = el('div');
    const title = el('div', 'pg-widget-header-title');
    title.textContent = this.branding?.clinicDisplayName ?? 'Chat with us';
    const sub = el('div', 'pg-widget-header-sub');
    sub.textContent = 'Usually replies in a few seconds';
    titleWrap.appendChild(title);
    titleWrap.appendChild(sub);
    const closeBtn = el('button', 'pg-widget-close');
    closeBtn.innerHTML = '&times;';
    closeBtn.setAttribute('aria-label', 'Close chat');
    closeBtn.addEventListener('click', () => this.toggle());
    header.appendChild(titleWrap);
    header.appendChild(closeBtn);

    this.messagesEl = el('div', 'pg-widget-messages');
    this.messagesEl.setAttribute('role', 'log');
    this.messagesEl.setAttribute('aria-live', 'polite');

    const inputBar = el('div', 'pg-widget-inputbar');
    this.input = el('textarea', 'pg-widget-input');
    this.input.rows = 1;
    this.input.placeholder = 'Type a message...';
    this.input.setAttribute('aria-label', 'Message');
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    });
    this.sendBtn = el('button', 'pg-widget-send');
    this.sendBtn.textContent = 'Send';
    this.sendBtn.style.background = this.branding?.primaryColor ?? '#2563eb';
    this.sendBtn.addEventListener('click', () => this.handleSend());
    inputBar.appendChild(this.input);
    inputBar.appendChild(this.sendBtn);

    this.panel.appendChild(header);
    this.panel.appendChild(this.messagesEl);
    this.panel.appendChild(inputBar);
    document.body.appendChild(this.panel);
    this.syncWaFloatClearance();
  }

  private addMessage(text: string, kind: 'assistant' | 'user' | 'error' | 'emergency') {
    const bubble = el('div', `pg-msg ${kind}`);
    bubble.textContent = text;
    if (kind === 'user') bubble.style.background = this.branding?.primaryColor ?? '#2563eb';
    this.messagesEl.appendChild(bubble);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  private setTyping(isTyping: boolean) {
    const existing = this.messagesEl.querySelector('.pg-widget-typing');
    if (existing) existing.remove();
    if (isTyping) {
      const typing = el('div', 'pg-widget-typing');
      typing.textContent = 'Typing...';
      this.messagesEl.appendChild(typing);
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    }
  }

  private async toggle() {
    this.isOpen = !this.isOpen;
    this.setLauncherIcon(this.isOpen);
    if (this.isOpen && !this.panel) {
      this.buildPanel();
      await this.ensureSession();
    }
    if (this.panel) {
      this.panel.classList.toggle('open', this.isOpen);
      if (this.isOpen) this.input.focus();
    }
  }

  private async ensureSession() {
    const stored = sessionStorage.getItem(this.storageKey);
    if (stored) {
      this.conversationId = stored;
      // A reconnect to an existing conversation doesn't re-hit the session
      // API (no server round-trip here at all), so there's no real,
      // conversation-state-aware option list available for it — showing the
      // static main menu again regardless of where the patient actually left
      // off would be exactly the kind of stale/wrong prompt the guided flow
      // is meant to avoid, so this branch intentionally renders no options.
      this.addMessage(this.branding?.welcomeMessage ?? 'Welcome back! How can I help?', 'assistant');
      return;
    }
    try {
      const session = await this.api.startSession();
      this.conversationId = session.conversationId;
      sessionStorage.setItem(this.storageKey, session.conversationId);
      this.addMessage(session.greeting, 'assistant');
      if (session.options?.length) this.renderOptions(session.options);
    } catch (err) {
      this.addMessage('Sorry, chat is temporarily unavailable. Please try again shortly.', 'error');
    }
  }

  /** Renders the server-driven option list that goes with whatever the
   * assistant just asked (main menu, doctor choice, reason, confirm/change,
   * ...) — see rule-engine/options.ts (packages/ai), the single source of
   * truth also used to build WhatsApp's numbered-list rendering of the same
   * data. Removed as soon as one is tapped (or the patient just starts
   * typing their own reply) so it never lingers once the conversation has
   * moved on. Clicking a button sends its exact label text through the
   * normal chat pipeline, same as if the patient had typed it — no separate
   * code path, so every safety/tool-calling rule still applies. */
  private renderOptions(options: ChatOptionDto[]) {
    const row = el('div', 'pg-quick-replies');
    for (const option of options) {
      const btn = el('button', 'pg-quick-reply-btn');
      btn.type = 'button';
      btn.textContent = option.label;
      btn.addEventListener('click', () => {
        row.remove();
        this.handleSend(option.label);
      });
      row.appendChild(btn);
    }
    this.messagesEl.appendChild(row);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  private async handleSend(presetText?: string) {
    const text = presetText ?? this.input.value.trim();
    if (!text || !this.conversationId) return;
    if (!presetText) this.input.value = '';
    this.messagesEl.querySelector('.pg-quick-replies')?.remove();
    this.addMessage(text, 'user');
    this.sendBtn.disabled = true;
    this.setTyping(true);
    try {
      const result = await this.api.sendMessage(this.conversationId, text);
      this.setTyping(false);
      if (result.emergencyTriggered) {
        this.addMessage(result.reply, 'emergency');
      } else {
        this.addMessage(result.reply, 'assistant');
      }
      if (result.options?.length) this.renderOptions(result.options);
    } catch (err) {
      this.setTyping(false);
      this.addMessage('Sorry, something went wrong sending that. Please try again.', 'error');
    } finally {
      this.sendBtn.disabled = false;
    }
  }
}

function boot() {
  const scriptEl =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>('script[data-widget-key]');

  if (!scriptEl) {
    console.error('[PractiGo widget] could not find the embedding <script> tag with data-widget-key.');
    return;
  }

  const widgetKey = scriptEl.getAttribute('data-widget-key');
  if (!widgetKey) {
    console.error('[PractiGo widget] data-widget-key attribute is required.');
    return;
  }

  const apiBaseUrl = scriptEl.getAttribute('data-api-base-url') ?? 'http://localhost:4000';
  const positionAttr = scriptEl.getAttribute('data-position');
  const position = positionAttr === 'bottom-left' ? 'left' : positionAttr === 'bottom-right' ? 'right' : undefined;

  const widget = new PractiGoWidget(widgetKey, apiBaseUrl, position);
  // Exposed so the host page can wire its own "Book an appointment" buttons to
  // window.practigoWidget.open() instead of relying solely on the floating launcher.
  (window as unknown as { practigoWidget: PractiGoWidget }).practigoWidget = widget;
  widget.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
